import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TransactionDialog from "@/components/TransactionDialog";
import TransactionList from "@/components/TransactionList";
import TransactionFilters from "@/components/TransactionFilters";
import RowActionButton, { RowActions } from "@/components/RowActionButton";
import GooglePaySyncDialog from "@/components/GooglePaySyncDialog";
import { CsvImportButton, CsvImportProgress } from "@/components/CsvImportStatus";
import { BulkSelectionBar, TransactionPagination } from "@/components/TransactionTableControls";
import {
  Plus, PencilSimple, Trash, DownloadSimple, Repeat, GoogleLogo, Tag, DotsThree, UploadSimple,
  ArrowsLeftRight,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CategoryIcon, categoryIndex, lookupCategory } from "@/lib/categoryIcons";
import { buildTransactionsTemplate } from "@/lib/csvTemplate";
import { createHttpLedgerAdapter, useLedgerInteraction } from "@/lib/ledgerInteraction";
import { useAccountScope } from "@/context/AccountScopeContext";
import { accountLabel } from "@/lib/accountScope";

const ledgerBackend = createHttpLedgerAdapter(api);

export default function Transactions() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { accounts, accountsById, scope, selectedAccount } = useAccountScope();
  const { state: ledgerState, ledger } = useLedgerInteraction(ledgerBackend, scope);
  const {
    items: txns, total, page, pages, categories, categoriesReady, loading,
    selectedIds, selectionMode, importingFile, deletingBulk, filters,
  } = ledgerState;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [gpayOpen, setGpayOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const fileRef = useRef(null);
  const showAccounts = accounts.length > 1;
  // New transactions land in the account being looked at, else the main one.
  const defaultAccountId = selectedAccount?.id
    || accounts.find((account) => ["main", "checking"].includes(account.type))?.id
    || accounts[0]?.id;
  const deleting = txns.find((transaction) => transaction.id === deleteId);

  useEffect(() => {
    if (!ledgerState.error) return;
    toast.error("Could not load transactions");
    ledger.clearError();
  }, [ledgerState.error, ledger]);

  useEffect(() => {
    if (!ledgerState.categoryError) return;
    toast.error("Could not load categories");
    ledger.clearCategoryError();
  }, [ledgerState.categoryError, ledger]);

  const changeFilter = (name, value) => {
    ledger.setFilter(name, value).catch(() => toast.error("Could not load transactions"));
  };

  const openNew = useCallback(() => { setEditing(null); setDialogOpen(true); }, []);
  const openEdit = (t) => { setEditing(t); setDialogOpen(true); };

  // "?new=1" is how the sidebar CTA opens this dialog. It has to react to every
  // navigation, not just the first mount: clicking the CTA while already on this
  // page changes the query string without remounting the page.
  useEffect(() => {
    if (params.get("new") !== "1") return;
    openNew();
    const next = new URLSearchParams(params);
    next.delete("new");
    setParams(next, { replace: true });
  }, [params, setParams, openNew]);

  const confirmDelete = async () => {
    try {
      await ledger.deleteOne(deleteId);
      toast.success("Transaction deleted");
      setDeleteId(null);
    } catch {
      toast.error("Could not delete");
    }
  };

  const toggleSelected = (id, checked) => ledger.toggleSelected(id, checked);
  const togglePage = (checked) => ledger.togglePage(checked);

  const confirmBulkDelete = async () => {
    try {
      const { deleted } = await ledger.deleteSelected();
      toast.success(`Deleted ${deleted} transaction${deleted === 1 ? "" : "s"}`);
      setBulkDeleteOpen(false);
    } catch {
      toast.error("Could not delete the selected transactions");
    }
  };

  // The filter is on the category *name*, which income and expense can share
  // ("Investments" exists on both sides), so the list is deduplicated by name
  // while each entry keeps an icon to show next to it.
  const catIndex = categoryIndex(categories);
  const allCats = [...(categories.expense || []), ...(categories.income || [])]
    .filter((c, i, list) => list.findIndex((o) => o.name === c.name) === i)
    .sort((a, b) => a.name.localeCompare(b.name));

  const downloadTemplate = () => {
    if (!categoriesReady) return;
    const blob = new Blob([buildTransactionsTemplate(categories)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "transactions_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { inserted, errors } = await ledger.importCsv(file);
      const into = showAccounts ? ` into ${accountsById[defaultAccountId]?.name}` : "";
      toast.success(`Imported ${inserted} transaction${inserted === 1 ? "" : "s"}${into}${errors.length ? `, ${errors.length} skipped` : ""}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed. Please try again.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const allPageSelected = txns.length > 0 && txns.every((transaction) => selectedIds.has(transaction.id));
  const somePageSelected = txns.some((transaction) => selectedIds.has(transaction.id));

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tighter">Transactions</h1>
          <p className="text-muted-foreground text-sm mt-1">{total.toLocaleString()} record{total === 1 ? "" : "s"}</p>
        </div>
        {/* Five buttons wrap onto three rows at 375px and push the ledger off
            the screen. Only Google Pay sync stays out on a phone; the rest move
            behind an overflow menu, and Add is already covered by the floating
            button in the layout. */}
        <div className="flex items-center gap-2 sm:flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" onChange={handleImport} className="hidden" data-testid="csv-file-input" />
          <Button size="sm" className="flex-1 bg-white text-black hover:bg-white/90 font-semibold gap-1.5 sm:flex-none" onClick={() => setGpayOpen(true)} data-testid="gpay-sync-btn">
            <GoogleLogo size={15} weight="bold" /> Sync Google Pay
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 border-border sm:hidden" aria-label="More actions" data-testid="txn-actions-menu">
                <DotsThree size={20} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-card border-border">
              <DropdownMenuItem onClick={() => navigate("/categories")} data-testid="menu-manage-categories">
                <Tag size={15} className="mr-2" /> Categories
              </DropdownMenuItem>
              <DropdownMenuItem onClick={downloadTemplate} disabled={!categoriesReady} data-testid="menu-csv-template">
                <DownloadSimple size={15} className="mr-2" /> CSV template
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => fileRef.current?.click()} disabled={Boolean(importingFile)} data-testid="menu-csv-import">
                <UploadSimple size={15} className="mr-2" /> {importingFile ? "Importing…" : "Import CSV"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden items-center gap-2 sm:flex">
            <Button variant="outline" size="sm" className="border-border gap-1.5" onClick={() => navigate("/categories")} data-testid="manage-categories-btn">
              <Tag size={15} /> Categories
            </Button>
            <Button variant="outline" size="sm" className="border-border gap-1.5" onClick={downloadTemplate} disabled={!categoriesReady} data-testid="csv-template-btn">
              <DownloadSimple size={15} /> Template
            </Button>
            <CsvImportButton
              importing={Boolean(importingFile)}
              onSelect={() => fileRef.current?.click()}
            />
            <Button size="sm" className="bg-income text-black hover:bg-income/90 font-semibold gap-1.5" onClick={openNew} data-testid="add-transaction-btn">
              <Plus size={15} weight="bold" /> Add
            </Button>
          </div>
        </div>
      </div>

      {importingFile && <CsvImportProgress fileName={importingFile} />}

      <TransactionFilters
        filters={filters}
        categories={allCats}
        onChange={changeFilter}
      />

      {/* Table */}
      <Card className="bg-card border-border rounded-lg overflow-hidden">
        <BulkSelectionBar
          selectedCount={selectedIds.size}
          deleting={deletingBulk}
            onClear={() => ledger.clearSelection()}
          onDelete={() => setBulkDeleteOpen(true)}
        />
        {loading ? (
          <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 bg-background" />)}</div>
        ) : txns.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-muted-foreground">No transactions found.</p>
            <Button size="sm" onClick={openNew} className="mt-4 bg-income text-black hover:bg-income/90 font-semibold gap-1.5">
              <Plus size={15} weight="bold" /> Add your first transaction
            </Button>
          </div>
        ) : (
          <>
          <TransactionList
            transactions={txns}
            categoryIndex={catIndex}
            accountsById={showAccounts ? accountsById : null}
            selectedIds={selectedIds}
            selectionMode={selectionMode}
            onEdit={openEdit}
            onToggle={toggleSelected}
            onEnterSelection={(id) => ledger.enterSelectionMode(id)}
          />
          <div className="hidden overflow-x-auto md:block">
            <Table data-testid="transactions-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-11 px-4">
                    <Checkbox
                      checked={allPageSelected ? true : somePageSelected ? "indeterminate" : false}
                      onCheckedChange={(checked) => togglePage(Boolean(checked))}
                      aria-label="Select all transactions on this page"
                      data-testid="select-page-transactions"
                    />
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Category</TableHead>
                  {showAccounts && <TableHead className="text-xs uppercase tracking-wider">Account</TableHead>}
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Amount</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txns.map((t) => (
                  <TableRow key={t.id} className={`border-border group ${selectedIds.has(t.id) ? "bg-expense/[0.04]" : ""}`} data-testid={`txn-row-${t.id}`}>
                    <TableCell className="w-11 px-4">
                      <Checkbox
                        checked={selectedIds.has(t.id)}
                        onCheckedChange={(checked) => toggleSelected(t.id, Boolean(checked))}
                        aria-label={`Select transaction from ${t.date}`}
                        data-testid={`select-${t.id}`}
                      />
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground whitespace-nowrap">{t.date}</TableCell>
                    <TableCell className="max-w-[240px] truncate">
                      <span className="flex items-center gap-1.5">
                        {t.recurring && <Repeat size={13} className="text-forecast shrink-0" />}
                        {t.description || <span className="text-muted-foreground">—</span>}
                      </span>
                    </TableCell>
                    <TableCell>
                      {t.transfer_id ? (
                        <Badge variant="outline" className="border-border font-normal gap-1.5 pl-1.5 text-forecast">
                          <ArrowsLeftRight size={13} />
                          <span className="text-foreground">Transfer</span>
                        </Badge>
                      ) : (() => {
                        const cat = lookupCategory(catIndex, t.type, t.category);
                        return (
                          <Badge
                            variant="outline"
                            className="border-border font-normal gap-1.5 pl-1.5"
                            style={cat ? { color: cat.color, borderColor: `${cat.color}59` } : undefined}
                          >
                            <CategoryIcon category={cat} size={13} />
                            <span className="text-foreground">{t.category}</span>
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    {showAccounts && (
                      <TableCell className="max-w-[200px] truncate text-muted-foreground">
                        {accountLabel(t, accountsById)}
                      </TableCell>
                    )}
                    <TableCell>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${t.type === "income" ? "text-income" : "text-expense"}`}>
                        {t.type}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${t.type === "income" ? "text-income" : "text-expense"}`}>
                      {t.type === "income" ? "+" : "−"}{eur(t.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions className="justify-end">
                        <RowActionButton onClick={() => openEdit(t)} aria-label={`Edit transaction from ${t.date}`} data-testid={`edit-${t.id}`}>
                          <PencilSimple size={15} />
                        </RowActionButton>
                        <RowActionButton danger onClick={() => setDeleteId(t.id)} aria-label={`Delete transaction from ${t.date}`} data-testid={`delete-${t.id}`}>
                          <Trash size={15} />
                        </RowActionButton>
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TransactionPagination
            page={page}
            pages={pages}
            total={total}
            loading={loading}
            onPage={(nextPage) => ledger.setPage(nextPage).catch(() => toast.error("Could not load transactions"))}
          />
          </>
        )}
      </Card>

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        categories={categories}
        editing={editing}
        onSave={(current, payload) => ledger.saveTransaction(current, payload)}
        onSaveTransfer={(transferId, payload) => ledger.saveTransfer(transferId, payload)}
        accounts={accounts}
        accountsById={accountsById}
        defaultAccountId={defaultAccountId}
        onFindTransferCandidates={(id) => ledger.transferCandidates(id)}
        onLinkTransfer={(id, otherId) => ledger.linkTransfer(id, otherId)}
        onUnlinkTransfer={(transferId) => ledger.unlinkTransfer(transferId)}
      />

      <GooglePaySyncDialog
        open={gpayOpen}
        onOpenChange={setGpayOpen}
        onPreview={(file) => ledger.previewGooglePay(file)}
        onCommit={(rows) => ledger.commitGooglePay(rows)}
      />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !deletingBulk && setBulkDeleteOpen(open)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size.toLocaleString()} transactions?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected transactions will be permanently removed, along with the other side
              of any transfer among them. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border" disabled={deletingBulk}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={deletingBulk}
              className="bg-expense text-white hover:bg-expense/90"
              data-testid="confirm-bulk-delete-btn"
            >
              {deletingBulk ? "Deleting…" : "Delete selected"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{deleting?.transfer_id ? "Delete transfer?" : "Delete transaction?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.transfer_id
                ? "Both sides of the transfer will be deleted. This action cannot be undone."
                : "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-expense text-white hover:bg-expense/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
